import { createRouter, createWebHistory } from 'vue-router'
import { supabase, getReadySession } from '@/lib/supabase'
import { isFeatureEnabled, loadFeatureFlags } from '@/lib/features'

const HomeView = () => import(/* webpackChunkName: "home" */ '../views/HomeView.vue')
const LoginView = () => import(/* webpackChunkName: "auth" */ '../views/LoginView.vue')
const RegisterView = () => import(/* webpackChunkName: "auth" */ '../views/RegisterView.vue')
const ForgotPasswordView = () => import(/* webpackChunkName: "auth" */ '../views/ForgotPasswordView.vue')
const ResetPasswordView = () => import(/* webpackChunkName: "auth" */ '../views/ResetPasswordView.vue')
const ChangePasswordView = () => import(/* webpackChunkName: "auth" */ '../views/ChangePasswordView.vue')
const ProfileView = () => import(/* webpackChunkName: "account" */ '../views/Settings.vue')
const PublicProfileView = () => import(/* webpackChunkName: "profile" */ '../views/PublicProfileView.vue')
const AuthCallback = () => import(/* webpackChunkName: "auth" */ '../views/AuthCallback.vue')
const FeedView = () => import(/* webpackChunkName: "feed", webpackPrefetch: true */ '../views/FeedView.vue')
const CommunityHighlightsView = () => import(/* webpackChunkName: "highlights", webpackPrefetch: true */ '../views/CommunityHighlightsView.vue')
const PostView = () => import(/* webpackChunkName: "feed" */ '../views/PostView.vue')
const NotificationsView = () => import(/* webpackChunkName: "notifications", webpackPrefetch: true */ '../views/NotificationsView.vue')
const MessagesView = () => import(/* webpackChunkName: "messages", webpackPrefetch: true */ '../views/MessagesView.vue')
const AdminDashboard = () => import(/* webpackChunkName: "admin-core" */ '../views/AdminDashboard.vue')
const ReportIssueView = () => import(/* webpackChunkName: "account" */ '../views/ReportIssueView.vue')
const TournamentRoomsView = () => import(/* webpackChunkName: "tournament-rooms" */ '../views/TournamentRoomsView.vue')
const TournamentRoomDetailView = () => import(/* webpackChunkName: "tournament-room" */ '../views/TournamentRoomDetailView.vue')
const LocalMeetsView = () => import(/* webpackChunkName: "meets", webpackPrefetch: true */ '../views/LocalMeetsView.vue')
const LocalMeetDetailView = () => import(/* webpackChunkName: "meets" */ '../views/LocalMeetDetailView.vue')
const DriveRadarView = () => import(/* webpackChunkName: "radar", webpackPrefetch: true */ '../views/DriveRadarView.vue')
const MemberCardView = () => import(/* webpackChunkName: "account" */ '../views/MemberCardView.vue')
const StickersView = () => import(/* webpackChunkName: "stickers", webpackPrefetch: true */ '../views/StickersView.vue')
const AdminStickerOrdersView = () => import(/* webpackChunkName: "admin-stickers" */ '../views/AdminStickerOrdersView.vue')
const LegalView = () => import(/* webpackChunkName: "legal" */ '../views/LegalView.vue')

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/login',
    name: 'login',
    meta: { auth: true },
    component: LoginView
  },
  {
    path: '/register',
    name: 'register',
    meta: { auth: true },
    component: RegisterView
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView
  },
  {
    path: '/change-password',
    name: 'change-password',
    meta: { requiresAuth: true },
    component: ChangePasswordView
  },
  {
    path: '/profile',
    name: 'profile',
    meta: { requiresAuth: true },
    component: ProfileView
  },
  {
    path: '/member-card',
    name: 'member-card',
    meta: { requiresAuth: true },
    component: MemberCardView
  },
  {
    path: '/stickers',
    name: 'stickers',
    meta: { requiresAuth: true, featureFlag: 'stickers_enabled' },
    component: StickersView
  },
  {
    path: '/feed',
    name: 'feed',
    meta: { requiresAuth: true },
    component: FeedView
  },
  {
    path: '/highlights',
    name: 'community-highlights',
    meta: { requiresAuth: true },
    component: CommunityHighlightsView
  },
  {
    path: '/post/:id',
    name: 'post',
    meta: { requiresAuth: true },
    component: PostView
  },
  {
    path: '/notifications',
    name: 'notifications',
    meta: { requiresAuth: true },
    component: NotificationsView
  },
  {
    path: '/messages/:handle?',
    name: 'messages',
    meta: { requiresAuth: true, featureFlag: 'messages_enabled' },
    component: MessagesView
  },
  {
    path: '/report',
    name: 'report',
    meta: { requiresAuth: true },
    component: ReportIssueView
  },
  {
    path: '/meets',
    name: 'meets',
    meta: { requiresAuth: true },
    component: LocalMeetsView
  },
  {
    path: '/radar',
    name: 'drive-radar',
    meta: { requiresAuth: true, featureFlag: 'drive_radar_enabled' },
    component: DriveRadarView
  },
  {
    path: '/meets/:id',
    name: 'meet-detail',
    meta: { requiresAuth: true },
    component: LocalMeetDetailView
  },
  {
    path: '/tournament',
    name: 'tournament',
    meta: { requiresAuth: true, featureFlag: 'tournament_enabled' },
    component: TournamentRoomsView
  },
  {
    path: '/tournament/room/:id',
    name: 'tournament-room',
    meta: { requiresAuth: true, featureFlag: 'tournament_enabled' },
    component: TournamentRoomDetailView
  },
  {
    path: '/admin',
    name: 'admin',
    meta: { requiresAdmin: true },
    component: AdminDashboard
  },
  {
    path: '/admin/sticker-orders',
    name: 'admin-sticker-orders',
    meta: { requiresAdmin: true },
    component: AdminStickerOrdersView
  },
  {
    path: '/u/:handle',
    name: 'public-profile',
    component: PublicProfileView
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: AuthCallback
  },
  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue')
  },
  {
    path: '/terms',
    name: 'terms',
    component: LegalView
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: LegalView
  },
  {
    path: '/cookies',
    name: 'cookies',
    component: LegalView
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return { ...savedPosition, behavior: 'smooth' }
    return { top: 0, left: 0, behavior: 'smooth' }
  }
})

router.beforeEach(async (to) => {
  if (to.meta?.featureFlag) {
    try {
      await loadFeatureFlags()
    } catch (_error) {
      // Fall back to cached defaults if the feature table cannot be read.
    }

    if (!isFeatureEnabled(to.meta.featureFlag)) {
      return { path: '/feed', replace: true }
    }
  }

  const session = await getReadySession()
  const user = session?.user

  if (to.path === '/' && user) {
    return { path: '/feed', replace: true }
  }

  if (to.meta?.requiresAuth && !user) {
    return { path: '/login', replace: true }
  }

  if (to.meta?.auth && user) {
    return { path: '/feed', replace: true }
  }

  if (to.meta?.requiresAdmin) {
    if (!user) {
      return { path: '/login', replace: true }
    }

    const { data: profile } = await supabase.from('profiles').select('role, status').eq('id', user.id).maybeSingle()

    if (!profile || profile.status === 'banned') {
      return { path: '/login', replace: true }
    }

    if (profile.role !== 'admin') {
      return { path: '/feed', replace: true }
    }
  }

  return true
})

// router.beforeResolve((to, from) => {
//   if (typeof window === 'undefined') return true
//   if (!from?.matched?.length) return true
//   if (to.fullPath === from.fullPath) return true
//   if (window.__drvnRouteReloadTarget === to.fullPath) {
//     window.__drvnRouteReloadTarget = null
//     return true
//   }
//
//   window.__drvnRouteReloadTarget = to.fullPath
//   window.location.assign(router.resolve(to).href)
//   return false
// })

export default router
